<template>
  <div>
    <form
      ref="downloadForm"
      method="post"
      :action="downloadUrl"
      style="display: none"
    >
      <input
        ref="downloadInput"
        name="Authorization"
        :value="authToken"
        type="hidden"
      />
    </form>
    <v-dialog
      v-model="openModal"
      @click:outside="handleCloseModal"
      width="640px"
    >
      <v-card class="v-card__download-modal">
        <v-toolbar
          class="v-toolbar__download-modal"
          elevation="0"
          color="background"
        >
          <v-toolbar-title
            class="text-h6 font-weight-medium pl-4"
            style="align-self: flex-start"
          >
            {{ $t('message.downloadVideoModal.title') }}
            <p class="text-subtitle__modal mb-0">
              {{ videoName }}
            </p>
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-toolbar-items class="align-self-baseline">
            <slot name="beforeCloseBtn"> </slot>
            <v-btn
              icon
              @click="handleCloseModal"
              class="no-ripple btn-close__modal pr-4"
            >
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-toolbar-items>
        </v-toolbar>
        <v-list class="original-lang__list">
          <v-list-item class="border-divider__modal">
            <v-list-item-avatar v-if="originalLang" class="avatar-flag__modal">
              <FlagCards
                :items="[getLangCodeBySrcLang(originalLang).country]"
              />
            </v-list-item-avatar>
            <v-list-item-avatar v-else class="avatar-flag__modal">
              <simple-svg
                :src="globeIcon"
                width="16px"
                height="16px"
                fill-class-name="fill-color"
                :fill="$vuetify.theme.dark ? '#757BA3' : '#8F92A3'"
                style="margin-left: 6px"
              />
            </v-list-item-avatar>
            <v-list-item-content>
              <v-list-item-title v-if="originalLang" class="text-file__modal">{{
                getLangCodeBySrcLang(originalLang).label
              }}</v-list-item-title>
              <v-list-item-title v-else class="text-file__modal">{{
                $t('message.downloadVideoModal.originalLang')
              }}</v-list-item-title>
              <v-list-item-subtitle class="text-subtitle__modal">{{
                addDecimal(
                  calcMegabytes(
                    storageSizeDetails[
                      originalAudioSelectedQuality == 'Original'
                        ? 'backup'
                        : originalAudioSelectedQuality || 'backup'
                    ],
                    'B',
                    'MB',
                    true
                  )
                )
              }}</v-list-item-subtitle>
            </v-list-item-content>
            <v-list-item-action class="d-flex flex-row">
              <base-select
                :v-model="originalAudioSelectedQuality"
                :items="originalAudioVideoQualities"
                :value="originalAudioDefaultQuality"
                @change="handleSelectedOption($event)"
                @click="handleBtnDisabledState(false, 'original')"
                size="medium"
                style="width: 169px; margin-right: 12px !important"
                dense
                hide-details
              />
              <div v-if="!isSubscriptionEnabledToDownload">
                <v-tooltip v-if="!isSubscriptionEnabledToDownload" bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <span v-bind="attrs" v-on="on">
                      <base-btn
                        :class="{
                          'no-pointer-events__download-modal':
                            originalAudioDownloadLoading,
                        }"
                        :style="
                          locale === 'pt'
                            ? 'min-width: 90px; max-width: 90px'
                            : 'min-width: 126px; max-width: 126px'
                        "
                        icon
                        size="default"
                        @click="downloadOriginalAudio()"
                        :disabled="!isSubscriptionEnabledToDownload"
                        type="tertiary"
                        :loading="originalAudioDownloadLoading"
                      >
                        <span
                          class="responsive-text__download-modal d-flex"
                          style="justify-content: center !important"
                        >
                          {{ $t('message.download') }}
                          <simple-svg
                            v-if="!originalAudioDownloadDisabled"
                            :src="downloadIcon"
                            width="16px"
                            height="16px"
                            fill-class-name="fill-color"
                            :fill="$vuetify.theme.dark ? '#666D99' : '#ABADBA'"
                          />
                        </span>
                      </base-btn>
                    </span>
                  </template>
                  <span>{{
                    $t('message.downloadVideoModal.disabledDownload')
                  }}</span>
                </v-tooltip>
              </div>
              <base-btn
                v-else-if="!originalAudioDownloadDisabled"
                :class="{
                  'no-pointer-events__download-modal':
                    originalAudioDownloadLoading,
                }"
                :style="
                  locale === 'pt'
                    ? 'min-width: 90px; max-width: 90px'
                    : 'min-width: 126px; max-width: 126px'
                "
                icon
                size="small"
                @click="downloadOriginalAudio()"
                :disabled="!isSubscriptionEnabledToDownload"
                type="tertiary"
                :loading="originalAudioDownloadLoading"
              >
                <span
                  class="responsive-text__download-modal d-flex"
                  style="justify-content: center !important"
                >
                  {{ $t('message.download') }}
                  <simple-svg
                    v-if="!originalAudioDownloadDisabled"
                    :src="downloadIcon"
                    width="16px"
                    height="16px"
                    fill-class-name="fill-color"
                    :fill="$vuetify.theme.dark ? '#C2C5D6' : '#3F404C'"
                  />
                </span>
              </base-btn>

              <v-tooltip v-else max-width="200px" top>
                <template v-slot:activator="{ on, attrs }">
                  <base-btn
                    v-bind="attrs"
                    v-on="{ ...on, ...$listeners }"
                    class="disabled-button__download-modal"
                    size="small"
                    type="tertiary"
                    :style="
                      locale === 'pt'
                        ? 'min-width: 90px; max-width: 90px'
                        : 'min-width: 126px; max-width: 126px'
                    "
                  >
                    <span
                      class="responsive-text__download-modal d-flex"
                      style="justify-content: center !important"
                    >
                      {{ $t('message.download') }}
                      <simple-svg
                        :src="downloadIcon"
                        width="16px"
                        height="16px"
                        fill-class-name="fill-color"
                        :fill="$vuetify.theme.dark ? '#C2C5D6' : '#3F404C'"
                      />
                    </span>
                  </base-btn>
                </template>
                <span>{{
                  $t('message.downloadVideoModal.awaitDownload')
                }}</span>
              </v-tooltip>
            </v-list-item-action>
          </v-list-item>
        </v-list>
        <v-list class="audio-files__list">
          <v-list-item
            class="border-divider__modal border-top-custom"
            v-for="(option, index) in availableAudios"
            :key="`${option.lang}-${index}`"
          >
            <v-list-item-avatar class="avatar-flag__modal">
              <FlagCards
                v-if="option && option.lang"
                :items="[handleLangCodeBySrcLang(option).country]"
              />
              <simple-svg
                v-else
                :src="globeIcon"
                width="16px"
                height="16px"
                fill-class-name="fill-color"
                :fill="$vuetify.theme.dark ? '#757BA3' : '#8F92A3'"
                style="margin-left: 6px"
              />
            </v-list-item-avatar>
            <v-list-item-content>
              <v-list-item-title class="text-file__modal">
                {{ handleLangCodeBySrcLang(option).label }}
                <v-tooltip bottom :disabled="!getAudioTag(option).tooltip">
                  <template v-slot:activator="{ on, attrs }">
                    <v-tag
                      type="neutral"
                      size="xsmall"
                      class="ml-2"
                      v-bind="attrs"
                      v-on="on"
                    >
                      <span class="body-regular-smaller neutral-20-dark--text">
                        {{ getAudioTag(option).text }}
                      </span>
                    </v-tag>
                  </template>
                  <span style="white-space: pre-line;">
                    {{ getAudioTag(option).tooltip }}
                  </span>
                </v-tooltip>
              </v-list-item-title>
              <v-list-item-subtitle
                v-if="option && option.lang"
                class="text-subtitle__modal"
                >{{
                  addDecimal(
                    calcMegabytes(
                      storageSizeDetails[
                        dubbedAudioSelectedQuality[index] ||
                          dubbedAudioDefaultQuality
                      ],
                      'B',
                      'MB',
                      true
                    )
                  )
                }}</v-list-item-subtitle
              >
            </v-list-item-content>
            <v-list-item-action class="d-flex flex-row">
              <base-select
                :v-model="dubbedAudioSelectedQuality[index]"
                :items="dubbedAudioVideoQualities"
                :value="dubbedAudioDefaultQuality"
                @change="handleSelectedQuality($event, index)"
                @click="handleBtnDisabledState(index, 'dubbed')"
                dense
                hide-details
                style="width: 169px; margin-right: 12px !important"
              ></base-select>
              <div v-if="!dubbedAudioDownloadDisabled[index]">
                <v-tooltip v-if="!isSubscriptionEnabledToDownload" bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <span v-bind="attrs" v-on="on">
                      <base-btn
                        :class="{
                          'no-pointer-events__download-modal':
                            dubbedAudioDownloadLoading[index],
                        }"
                        icon
                        size="small"
                        :style="
                          locale === 'pt'
                            ? 'min-width: 90px; max-width: 90px'
                            : 'min-width: 126px; max-width: 126px'
                        "
                        @click="downloadDubbedAudio(index, option)"
                        type="tertiary"
                        :disabled="!isSubscriptionEnabledToDownload"
                        :loading="dubbedAudioDownloadLoading[index]"
                      >
                        <span
                          class="responsive-text__download-modal d-flex"
                          style="justify-content: center !important"
                        >
                          {{ $t('message.download') }}
                          <simple-svg
                            :src="downloadIcon"
                            width="16px"
                            height="16px"
                            fill-class-name="fill-color"
                            :fill="$vuetify.theme.dark ? '#666D99' : '#ABADBA'"
                          />
                        </span>
                      </base-btn>
                    </span>
                  </template>
                  <span>{{
                    $t('message.downloadVideoModal.disabledDownload')
                  }}</span>
                </v-tooltip>
                <base-btn
                  v-else
                  :class="{
                    'no-pointer-events__download-modal':
                      dubbedAudioDownloadLoading[index],
                  }"
                  icon
                  size="small"
                  :style="
                    locale === 'pt'
                      ? 'min-width: 90px; max-width: 90px'
                      : 'min-width: 126px; max-width: 126px'
                  "
                  @click="downloadDubbedAudio(index, option)"
                  type="tertiary"
                  :disabled="!isSubscriptionEnabledToDownload"
                  :loading="dubbedAudioDownloadLoading[index]"
                >
                  <span
                    class="responsive-text__download-modal d-flex"
                    style="justify-content: center !important"
                  >
                    {{ $t('message.download') }}
                    <simple-svg
                      :src="downloadIcon"
                      width="16px"
                      height="16px"
                      fill-class-name="fill-color"
                      :fill="$vuetify.theme.dark ? '#C2C5D6' : '#3F404C'"
                    />
                  </span>
                </base-btn>
              </div>
              <v-tooltip v-else max-width="200px" top>
                <template v-slot:activator="{ on, attrs }">
                  <base-btn
                    v-bind="attrs"
                    v-on="{ ...on, ...$listeners }"
                    class="disabled-button__download-modal"
                    size="small"
                    :style="
                      locale === 'pt'
                        ? 'min-width: 90px; max-width: 90px'
                        : 'min-width: 126px; max-width: 126px'
                    "
                    type="tertiary"
                  >
                    <span
                      class="responsive-text__download-modal d-flex"
                      style="justify-content: center !important"
                    >
                      {{ $t('message.download') }}
                      <simple-svg
                        :src="downloadIcon"
                        width="16px"
                        height="16px"
                        fill-class-name="fill-color"
                        :fill="$vuetify.theme.dark ? '#C2C5D6' : '#3F404C'"
                      />
                    </span>
                  </base-btn>
                </template>
                <span>{{
                  $t('message.downloadVideoModal.awaitDownload')
                }}</span>
              </v-tooltip>
            </v-list-item-action>
          </v-list-item>
        </v-list>
      </v-card>
    </v-dialog>
    <warning-download-modal
      :openWarningDownload="openWarningDownload"
      :videoName="videoName"
      :videoId="videoId"
      :availableAudios="availableAudios"
      :availableVideoQualities="availableVideoQualities"
      :originalLang="originalLang"
      :downloadLoadingStatus="downloadLoadingStatus"
      :storageSizeDetails="storageSizeDetails"
      :notCloseModal="notCloseModal"
    ></warning-download-modal>
  </div>
</template>
<script>
import FlagCards from '@/components/FlagCards.vue'
import BaseBtn from '@/components/BaseBtn.vue'
import BaseSelect from '@/components/BaseComponents/BaseSelect.vue'
import langCodes from '@/utils/langCodes.mixin'
import downloadUrl from '@/utils/downloadUrl.mixin'
import i18n from '@/translations/lang'
import { mapMutations, mapState } from 'vuex'

import globeIcon from '@/assets/pandaIcons/globe.svg'
import downloadIcon from '@/assets/pandaIcons/download.svg'
import WarningDownloadModal from './WarningDownloadModal.vue'
import calcMegabytesMixin from '@/utils/calcMegabytes'
import Storage from '@/utils/Storage'

export default {
  name: 'DownloadVideoModal',
  components: { FlagCards, BaseBtn, BaseSelect, WarningDownloadModal },
  mixins: [langCodes, downloadUrl, calcMegabytesMixin],
  data() {
    return {
      openModal: null,
      loadingControl: null,
      dubbedAudioSelectedQuality: [],
      dubbedAudioDownloadLoading: [],
      dubbedAudioDownloadDisabled: [],
      dubbedAudioDefaultQuality: '',
      dubbedAudioVideoQualities: [],
      originalAudioSelectedQuality: '',
      notCloseModal: false,
      originalAudioDownloadLoading: false,
      originalAudioVideoQualities: [],
      originalAudioDefaultQuality: 'Original',
      originalAudioDownloadDisabled: false,
      openWarningDownload: false,
      globeIcon,
      downloadIcon,
      authToken: '',
      qualityToBeDownloaded: null,
      localVideoId: '',
    }
  },
  props: {
    openDownloadVideoModal: {
      type: Boolean,
      default: false,
    },
    videoName: {
      type: String,
      default: '',
    },
    videoExtension: {
      type: String,
      default: '',
    },
    videoId: {
      type: String,
      default: '',
    },
    originalLang: {
      type: String,
      default: '',
    },
    availableAudios: {
      type: Array,
    },
    availableVideoQualities: {
      type: Array,
    },
    downloadLoadingStatus: {
      type: Boolean,
      default: false,
    },
    storageSizeDetails: {
      type: Object,
    },
    preDownloadWarning: {
      type: Boolean,
      default: true,
    },
  },
  computed: {
    ...mapState({
      userPlanInfo: (state) => state.user.userPlanInfo,
      userInfo: (state) => state.user.userInfo,
    }),
    locale() {
      return this.$i18next.resolvedLanguage
    },
    isSubscriptionEnabledToDownload() {
      const blockDownloadStatus = ['unpaid', 'canceled']
      let isDownloadAllowed = true
      if (
        this.userPlanInfo.subscription &&
        this.userPlanInfo.subscription.metadata.trusted_download
      ) {
        const trustedDownloadDate = this.userPlanInfo.subscription_metadata
          ?.trusted_download
          ? this.formatDateFromBr(
              this.userPlanInfo.subscription_metadata.trusted_download
            )
          : null
        isDownloadAllowed =
          !trustedDownloadDate || trustedDownloadDate >= new Date()
      }
      return (
        this.userPlanInfo.subscription &&
        !blockDownloadStatus.includes(this.userPlanInfo.subscription.status) &&
        isDownloadAllowed
      )
    },
  },
  watch: {
    openDownloadVideoModal(val) {
      if (!this.preDownloadWarning) {
        this.openModal = val
        this.notCloseModal = true
        return
      }
      if (
        localStorage.getItem(`isUnderstandDownload-${this.userInfo.sub}`) ||
        !localStorage.getItem(`countDownload-${this.userInfo.sub}`) ||
        Number(localStorage.getItem(`countDownload-${this.userInfo.sub}`)) < 4
      ) {
        this.openModal = val
      } else {
        this.openWarningDownload = val
      }
    },
    downloadLoadingStatus(val) {
      if (this.loadingControl === 'backup') {
        if (val) {
          setTimeout(() => {
            this.originalAudioDownloadLoading = !val
            this.originalAudioDownloadDisabled = val
            this.updateSnackBar()
          }, 3000)
        }
        return (this.originalAudioDownloadLoading = val)
      }
      this.$set(this.dubbedAudioDownloadLoading, this.loadingControl, val)
      if (val) {
        setTimeout(() => {
          this.$set(this.dubbedAudioDownloadLoading, this.loadingControl, !val)
          this.$set(this.dubbedAudioDownloadDisabled, this.loadingControl, val)
          this.updateSnackBar()
        }, 3000)
      }
    },
    availableVideoQualities(qualitiesArray) {
      this.originalAudioVideoQualities = qualitiesArray.concat([
        this.originalAudioDefaultQuality,
      ])
      this.dubbedAudioVideoQualities = qualitiesArray
      this.dubbedAudioDefaultQuality = qualitiesArray[0]
    },
    availableAudios(val) {
      this.dubbedAudioDownloadLoading = val.map(() => false)
      this.dubbedAudioDownloadDisabled = val.map(() => false)
    },
  },
  async mounted() {
    this.authToken = Storage.getItem('accessToken')
    this.$root.$emit('closeDownloadVideoModal')
    this.$root.$on('closeWarningDownload', () => {
      this.openWarningDownload = false
    })
    this.$root.$on('openModalDownloadVideo', () => {
      this.openModal = true
    })
    window.addEventListener('keydown', this.escape)
  },
  beforeDestroy() {
    this.$root.$off('openDownloadVideoModal')
    this.$root.$off('closeDownloadVideoModal')
    window.removeEventListener('keydown', this.escape)
  },
  methods: {
    ...mapMutations('user', ['UPDATE_GLOBAL_SNACKBAR']),
    getAudioTag(option) {
      if (!option?.type) return null

      const isUploaded = option.type === 'UPLOADED'
      const isGenerated = option.type === 'GENERATED'

      if (isUploaded) {
        return {
          text: this.$t('message.ai.dubbings.sent'),
          tooltip: this.$t('message.ai.dubbings.sentTooltip'),
        }
      }

      if (isGenerated) {
        const isLipSync = option.is_lipsync

        return {
          text: this.$t(
            isLipSync
              ? 'message.ai.dubbings.dubbingLipSync'
              : 'message.ai.dubbings.dubbingAI'
          ),
          tooltip: this.$t(
            isLipSync
              ? 'message.ai.dubbings.dubbingLipSyncTooltip'
              : 'message.ai.dubbings.dubbingAITooltip'
          ),
        }
      }

      return null
    },
    escape(event) {
      if (event.key === 'Escape') this.handleCloseModal()
    },
    formatDateFromBr(dateString) {
      if (!dateString) return null
      const [day, month, year] = dateString.split('/')
      return new Date(Date.UTC(year, month - 1, day))
    },
    handleCloseModal() {
      this.dubbedAudioDownloadLoading = this.availableAudios.map(() => false)
      this.dubbedAudioDownloadDisabled = this.availableAudios.map(() => false)
      this.originalAudioDownloadDisabled = false
      this.$root.$emit('closeDownloadVideoModal')
    },
    async downloadDubbedAudio(index, option) {
      const userSub = this.userInfo.sub
      if (this.hasExceededDownloadLimits(userSub)) {
        this.notCloseModal = true
        return (this.openWarningDownload = true)
      }

      this.loadingControl = index

      if (option.is_lipsync && option.video_id) {
        return this.handleLipsyncDownload(index, option.video_id, option.lang)
      }

      this.handleStandardDownload(index, option.lang)
    },
    handleStandardDownload(index, lang) {
      const quality = this.dubbedAudioSelectedQuality[index] ?? this.dubbedAudioDefaultQuality
      const url = this.downloadDubUrl(this.videoId, quality, lang)

      this.setCountDownload(quality)
      this.$root.$emit('handleVideoDownload', url)
    },
    async handleLipsyncDownload(index, lipsyncVideoId, lang) {
      const selectedQuality = this.dubbedAudioSelectedQuality[index] ?? this.dubbedAudioDefaultQuality
      this.qualityToBeDownloaded = selectedQuality

      const videoNameWithoutExtension = this.videoName.replace(`.${this.videoExtension}`, '')
      const title = `${videoNameWithoutExtension}_${selectedQuality}_${lang}_lipsync`

      const url = this.downloadLipsyncUrl(lipsyncVideoId, title)

      this.setCountDownload(selectedQuality)
      this.$root.$emit('handleVideoDownload', url)
    },
    downloadOriginalAudio() {
      const userSub = this.userInfo.sub

      if (this.hasExceededDownloadLimits(userSub)) {
        this.notCloseModal = true
        return (this.openWarningDownload = true)
      }

      this.loadingControl = 'backup'

      const selectedQuality =
        this.originalAudioSelectedQuality ?? this.originalAudioDefaultQuality
      const qualityToDownload = selectedQuality === 'Original' ? '' : selectedQuality

      const formattedDownloadUrl = this.downloadDubUrl(
        this.videoId,
        qualityToDownload
      )

      this.setCountDownload(selectedQuality)
      this.$root.$emit('handleVideoDownload', formattedDownloadUrl)
    },
    hasExceededDownloadLimits(userSub) {
      const maxDownloadSize = 100 * 1024 * 1024 * 1024

      const isUnderstood = localStorage.getItem(`isUnderstandDownload-${userSub}`)
      const countDownload = Number(localStorage.getItem(`countDownload-${userSub}`)) || 0
      const totalDownloadSize = Number(localStorage.getItem(`totalDownloadSize-${userSub}`)) || 0

      const reachedDownloadCountLimit = !isUnderstood && countDownload >= 4
      const exceededSizeLimit = totalDownloadSize > maxDownloadSize

      return reachedDownloadCountLimit || exceededSizeLimit
    },
    setCountDownload(sizeDownload) {
      const key = sizeDownload === 'Original' ? 'backup' : sizeDownload
      const sizeSelected = this.storageSizeDetails[key]
      const currentCount =
        Number(localStorage.getItem(`countDownload-${this.userInfo.sub}`)) || 0
      if (currentCount < 5) {
        localStorage.setItem(
          `countDownload-${this.userInfo.sub}`,
          currentCount + 1
        )
      }
      const currentSize =
        Number(
          localStorage.getItem(`totalDownloadSize-${this.userInfo.sub}`)
        ) || 0
      const newSize = currentSize + Number(sizeSelected || 0)

      localStorage.setItem(`totalDownloadSize-${this.userInfo.sub}`, newSize)
    },
    addDecimal(lengthString) {
      let [numberPart, unitPart] = lengthString.split(' ')

      const numericValue = Number(numberPart)
      const formattedNumber = isNaN(numericValue) ? 0 : numericValue.toFixed(1)
      const unitLabel = unitPart || ''

      return `${formattedNumber} ${unitLabel}`.trim()
    },
    formatLabel(type) {
      if (type === 'GENERATED')
        return i18n.t('message.downloadVideoModal.dubbingPanda')
      if (type === 'UPLOADED')
        return i18n.t('message.downloadVideoModal.audioFile')
    },
    handleLangCodeBySrcLang(option) {
      const lang = option.lang === 'pt' ? 'pt-BR' : option.lang
      return this.getLangCodeBySrcLang(lang)
    },
    handleSelectedQuality(val, index) {
      this.dubbedAudioSelectedQuality[index] = val
      this.$set(this.dubbedAudioDownloadDisabled, index, false)
    },
    handleSelectedOption(val) {
      this.originalAudioSelectedQuality = val
      this.originalAudioDownloadDisabled = false
    },
    handleBtnDisabledState(index, param) {
      if (
        param === 'original' &&
        this.originalAudioVideoQualities.length === 1
      ) {
        this.originalAudioDownloadDisabled = false
        return
      }
      if (param === 'dubbed' && this.dubbedAudioVideoQualities.length === 1) {
        this.$set(this.dubbedAudioDownloadDisabled, index, false)
        return
      }
    },
    updateSnackBar() {
      this.UPDATE_GLOBAL_SNACKBAR({
        open: true,
        text: i18n.t('message.downloadVideoModal.awaitDownload'),
        type: 'info',
      })
    },
  },
}
</script>
<style lang="scss">
.text-subtitle__modal {
  color: varFigma(neutral-40-light) !important;
  font-weight: 400;
  font-size: 12px;
  line-height: 18px;
}
.text-file__modal {
  color: varFigma(neutral-20-dark) !important;
  font-weight: 400;
  font-size: 12px;
  line-height: 18px;
}
.avatar-flag__modal {
  scale: 1.3;
  min-width: unset !important;
  width: unset !important;
}
.v-toolbar__download-modal .v-toolbar__content {
  padding: 0;
}
.v-card__download-modal {
  padding: 24px 16px;
}
.btn-close__modal {
  justify-content: flex-end !important;
  align-items: flex-start !important;
  padding-top: 4px !important;
}
.v-card__download-modal
  .v-list-item__action.d-flex.flex-row.v-list-item__action--stack {
  align-items: center !important;
}
.original-lang__list {
  padding-bottom: 0;
}
.audio-files__list {
  padding-top: 0;
}
.disabled-button__download-modal {
  cursor: not-allowed !important;
  opacity: 0.3;
}
.no-pointer-events__download-modal {
  pointer-events: none !important;
}
.responsive-text__download-modal.d-flex {
  align-items: center !important;
  justify-content: center !important;
  height: 20px !important;
  line-height: 20px !important;
  display: flex !important;
}
.responsive-text__download-modal.d-flex svg {
  margin-left: 6px !important;
  align-self: center !important;
}
.border-top-custom {
  border-top: 1px solid varFigma(neutral-95-lightest);
}
</style>
